/**
 * JourneyInstanceGuard
 *
 * Reusable guard component for hero pages that require an active journey instance.
 * Instead of silently redirecting, it renders the AppShellLayout with an in-page
 * message explaining that a journey is required, with CTAs to:
 *   - Select a journey (if user has instances)
 *   - Build a journey (if user has none)
 *
 * This pattern is used by all hero routes (/checkup, /teleportal, /qultureup, /care, /passport)
 * and can be replicated for any route that requires a journey context.
 *
 * Usage:
 *   <JourneyInstanceGuard>{children}</JourneyInstanceGuard>
 */

import { lazy, ReactNode, Suspense } from 'react';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent } from '@tonnie/ui';
import { AlertCircle, MapPin, Rocket } from '@tonnie/assets';
import { Link } from 'wouter';
import { motion } from 'framer-motion';

import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';

/**
 * The residence form a waiting journey opens with — lazy, so the route table
 * (which imports this guard eagerly) does not carry the country and city
 * fields for the rare journey that needs them.
 */
const PendingJourneyResidence = lazy(
  () => import('@/features/platform/first-run/components/PendingJourneyResidence')
);

const GUARD_ANIMATION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 },
};

function HeroPageSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-8 w-48 rounded-lg bg-muted" />
          <div className="h-4 w-64 rounded bg-muted" />
        </div>
        <div className="h-10 w-32 rounded-lg bg-muted" />
      </div>
      <Card className="border-border/50 overflow-hidden">
        <CardContent className="p-8">
          <div className="flex gap-6">
            <div className="w-24 h-24 rounded-full bg-muted shrink-0" />
            <div className="flex-1 space-y-3">
              <div className="h-6 w-40 rounded bg-muted" />
              <div className="h-4 w-full rounded bg-muted" />
              <div className="h-4 w-3/4 rounded bg-muted" />
              <div className="flex gap-2 mt-4">
                <div className="h-8 w-20 rounded-lg bg-muted" />
                <div className="h-8 w-20 rounded-lg bg-muted" />
                <div className="h-8 w-20 rounded-lg bg-muted" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-muted" />
        ))}
      </div>
    </div>
  );
}

interface JourneyInstanceGuardProps {
  children: ReactNode;
}

export function JourneyInstanceGuard({ children }: JourneyInstanceGuardProps) {
  const { t } = useTranslation();
  const { activeInstance, instances, isLoading, isError } = useJourneyInstance();

  // Show skeleton while journey context loads — prevents hero pages from
  // mounting before context is ready (avoids "first take" state flash)
  if (isLoading) {
    return <HeroPageSkeleton />;
  }

  // ── A journey waiting to open ──────────────────────────────────────
  // BL-032: a `pending_setup` journey waits on the citizen's residence. It is
  // asked right here (NOT /journey/builder, a separate flow): saving it
  // activates the journey, and this guard re-evaluates on the refreshed list.
  if (activeInstance?.lifecycleState === 'pending_setup') {
    return (
      <Suspense fallback={<HeroPageSkeleton />}>
        <PendingJourneyResidence />
      </Suspense>
    );
  }

  // ── Active instance is non-usable lifecycle ────────────────────────
  // Cornerstone refactor 2026-05: an instance can be selected but in a
  // lifecycle state that the backend journey-access guard rejects
  // (`paused`, `expired`, `cancelled`). Show a dedicated empathetic
  // message instead of letting the user hit a backend 403 deeper in the
  // page.
  if (activeInstance && activeInstance.lifecycleState !== 'active') {
    const state = activeInstance.lifecycleState;
    const stateCopy: {
      title: string;
      description: string;
      cta: { label: string; href: string };
    } =
      state === 'paused'
        ? {
            title: t('journey.guard.pausedTitle'),
            description: t('journey.guard.pausedDescription'),
            cta: { label: t('journey.guard.viewBilling'), href: '/billing' },
          }
        : state === 'expired'
          ? {
              title: t('journey.guard.expiredTitle'),
              description: t('journey.guard.expiredDescription'),
              cta: { label: t('journey.guard.renewJourney'), href: '/billing' },
            }
          : {
              title: t('journey.guard.unavailableTitle'),
              description: t('journey.guard.unavailableDescription'),
              cta: {
                label: t('journey.guard.buildNew'),
                href: '/journey/builder?new=1',
              },
            };

    return (
      <motion.div
        className="flex items-center justify-center min-h-[60vh] p-6"
        {...GUARD_ANIMATION}
      >
        <Card className="border-border/50 max-w-lg w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-tone-amber-subtle flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-tone-amber" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">{stateCopy.title}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
              {stateCopy.description}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button className="w-full sm:w-auto" asChild>
                <Link href={stateCopy.cta.href}>{stateCopy.cta.label}</Link>
              </Button>
              <Button variant="outline" className="w-full sm:w-auto" asChild>
                <Link href="/dashboard">{t('journey.guard.goToDashboard')}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Active instance selected & active — render children normally
  if (activeInstance) {
    return <>{children}</>;
  }

  // ── Journeys failed to load ────────────────────────────────────────
  // The list query terminally errored (after the SDK's retry policy) and we
  // have no cached instance to fall back to. This is NOT the same as "the
  // user has no journeys" — showing the "Build a journey" CTA here would be a
  // data-loss-shaped lie that pushes a user with a real journey to create a
  // duplicate. Surface an honest, recoverable error instead.
  if (isError && instances.length === 0) {
    return (
      <motion.div
        className="flex items-center justify-center min-h-[60vh] p-6"
        {...GUARD_ANIMATION}
      >
        <Card className="border-border/50 max-w-lg w-full shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-destructive-accessible" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {t('journey.guard.loadErrorTitle')}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
              {t('journey.guard.loadErrorDescription')}
            </p>
            <div className="flex justify-center">
              <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // ── No active journey instance ─────────────────────────────────────
  const hasInstances = instances.length > 0;

  return (
    <motion.div className="flex items-center justify-center min-h-[60vh] p-6" {...GUARD_ANIMATION}>
      <Card className="border-border/50 max-w-lg w-full shadow-lg">
        <CardContent className="p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            {hasInstances ? (
              <MapPin className="w-10 h-10 text-primary-accessible" />
            ) : (
              <Rocket className="w-10 h-10 text-primary-accessible" />
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {hasInstances ? t('journey.guard.selectTitle') : t('journey.guard.buildTitle')}
          </h2>

          {/* Description */}
          <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
            {hasInstances
              ? t('journey.guard.selectDescription')
              : t('journey.guard.buildDescription')}
          </p>

          {/* Alert for "has instances but none active" */}
          {hasInstances && (
            <div className="flex items-start gap-3 p-3 bg-tone-amber-subtle border border-tone-amber-line rounded-lg mb-6 text-left max-w-md mx-auto">
              <AlertCircle className="w-5 h-5 text-tone-amber mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-tone-amber">
                  {t('journey.guard.noActiveTitle')}
                </p>
                <p className="text-xs text-tone-amber mt-0.5">
                  {t('journey.guard.noActiveDescription')}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button className="w-full sm:w-auto" asChild>
              <Link href="/journey/builder?new=1">
                <Rocket className="w-4 h-4 mr-2" />
                {hasInstances ? t('journey.guard.buildNew') : t('journey.guard.buildFirst')}
              </Link>
            </Button>
            {hasInstances && (
              <Button variant="outline" className="w-full sm:w-auto" asChild>
                <Link href="/dashboard">{t('journey.guard.goToDashboard')}</Link>
              </Button>
            )}
          </div>

          {/* Helper text */}
          <p className="text-xs text-muted-foreground mt-4">
            {hasInstances ? t('journey.guard.selectorHint') : t('journey.guard.builderHint')}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}
